import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {useInitAiPromptBox} from '@Components/ai-prompt-box/hooks/useInitAiPromptBox';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import {AiPromptBoxTemplatesButton} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-templates-button/ai-prompt-box-templates-button';
import {authenticateUserForAiChat, checkDailyLimit, onAiPromptBoxSubmit} from '@Libraries/ai-chat.library';
import {getDefaultAttachments} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {getTeamCreditsForUser, isUserLoggedIn} from '@Libraries/user.library';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import type {CreateWithAiPromptBoxProps} from './create-with-ai-prompt-box.types';

/**
 * A "create from scratch" AI prompt box: the user describes what they want, optionally attaches
 * uploads/designs, and submitting starts an AI chat session (no template redesign target). Shared by
 * the marketing AI-chat page and the /l/ landing-page section — callers vary the prompt-box id, the
 * analytics source values, and whether to authenticate the visitor before submitting.
 */
export function CreateWithAiPromptBox({promptBoxId, chatSource, startSource, blockClasses, inputClasses, showTemplatesButton = false, authenticateBeforeSubmit = false, showInitialAnimation = true}: CreateWithAiPromptBoxProps): ReactElement {
  const {aiPromptBox, fetchedCredits} = useInitAiPromptBox(promptBoxId);
  const {actions} = aiPromptBox;

  useEffect(() => {
    void checkDailyLimit(actions.update);
    actions.update({size: AiPromptBoxSize.SMALL_EXTENDED});
  }, []);

  const startAiChat = async (credits: number | null | undefined): Promise<void> => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_START, {[GA4EventParamName.SOURCE]: startSource});
    await onAiPromptBoxSubmit(aiPromptBox, credits, []);
  };

  const submit = async (): Promise<void> => {
    if (!authenticateBeforeSubmit) {
      await startAiChat(fetchedCredits);
      return;
    }
    const wasLoggedInBeforeAuth = isUserLoggedIn();
    await authenticateUserForAiChat(async () => {
      const credits = wasLoggedInBeforeAuth ? fetchedCredits : (await getTeamCreditsForUser()).credits;
      await startAiChat(credits);
    });
  };

  return (
    <AiPromptBox
      id={promptBoxId}
      blockClasses={blockClasses}
      inputClasses={inputClasses}
      onGenerate={submit}
      initialConfig={{
        prompt: '',
        size: AiPromptBoxSize.SMALL_EXTENDED,
        placeholder: window.i18next.t('pmwjs_describe_the_design_you_want_to_create_elipses'),
        promptBoxState: PromptBoxState.IDLE,
        attachments: getDefaultAttachments(),
        source: chatSource,
        transcription: {showMic: true},
        showInitialAnimation,
      }}
      onRegenerate={submit}
      options={showTemplatesButton ? [AiPromptBoxUploads, AiPromptBoxTemplatesButton] : [AiPromptBoxUploads]}
      onFocus={() => {
        void checkDailyLimit(actions.update);
      }}
    />
  );
}
